import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import Layout from '../components/Layout';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/hooks/use-toast';
import axios from 'axios';

export type JobInfo = {
  title: string;
  company: string;
  description: string;
  location?: string;
  rawJobText?: string;
  url?: string;
};

// Helper function to fix malformed URLs
const fixUrl = (url: string | null): string | null => {
  if (!url) return null;
  
  // Fix case where colon is not followed by double slashes
  if (/^https?:[^\/]/.test(url)) {
    url = url.replace(/^(https?:)/, '$1//');
  }
  
  // Fix case where only one slash follows the colon
  if (/^https?:\/[^\/]/.test(url) && !/^https?:\/\//.test(url)) {
    url = url.replace(/^(https?:\/)(.)/, '$1/$2');
  }
  
  // Ensure https:// is present if missing but looks like a domain
  if (!/^https?:\/\//.test(url) && /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(url)) {
    url = `https://${url}`;
  }
  return url;
};

const NewCoverLetterPage: React.FC = () => {
  
  const toast = useToast();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation(); // Add location to help with debugging
  const { user, loading: authLoading } = useAuth();
  


  const [status, setStatus] = useState<'idle' | 'fetching-job' | 'generating' | 'complete' | 'error'>('idle');
  const [jobInfo, setJobInfo] = useState<JobInfo | null>(null);
  const [coverLetter, setCoverLetter] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null); // Store the URL being processed
  
  // Add more robust tracking refs
  const processingStarted = useRef(false);
  const navigateTriggered = useRef(false);
  
  useEffect(() => {
    
    // Only run this effect once after initial auth check and if processing hasn't started
    if (authLoading || processingStarted.current) {
      return;
    }

    const initializeProcess = async () => {
      // Mark processing as started immediately to prevent double processing
      processingStarted.current = true; 

      setErrorMessage(null); 

      const rawJobUrl = searchParams.get('jobUrl');
      
      // Special case to prevent early redirect
      if (!rawJobUrl) {
        console.error('[NewCoverLetterPage] No jobUrl parameter found in URL');
        setErrorMessage('No job URL provided. Please use a MakeCL link or enter a job description directly.');
        setStatus('error');
        processingStarted.current = false; // Reset if error
        return;
      }
      
      const correctedUrl = fixUrl(rawJobUrl);
      setProcessedUrl(correctedUrl); // Store for display and debugging

      if (!correctedUrl) {
        console.error('[NewCoverLetterPage] Failed to process job URL');
        setErrorMessage('Could not process the provided job URL. Please try again with a valid URL.');
        setStatus('error');
        processingStarted.current = false; // Reset if error
        return;
      }

      if (!user) {
        // Save this exact URL to return after login
        const redirectUrl = `${window.location.pathname}${window.location.search}`;
        localStorage.setItem('qcl-redirect-url', redirectUrl);
        
        // Trigger Supabase OAuth flow with explicit redirectTo that includes the current URL
        await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth-callback?return_to=${encodeURIComponent(redirectUrl)}`
          }
        });
        return;
      }

      // If we reach here, both authentication and URL are valid

      await fetchJobDetails(correctedUrl);
    };

    initializeProcess().catch(error => {
      console.error('[NewCoverLetterPage] Unhandled error in initialization:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      setStatus('error');
      processingStarted.current = false;
    });

  // Only run when authentication state or URL parameters change
  }, [authLoading, user, searchParams]);
  
  // Function to fetch job details from the backend
  const fetchJobDetails = async (url: string) => {
    try {

      setStatus('fetching-job');
      
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        throw new Error('No active session');
      }
      
      // Use REACT_APP_API_URL for the base API path if available
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      // Ensure the endpoint starts with /api/
      const apiUrl = `${apiBaseUrl.replace(/\/api$/, '')}/api/job-details?url=${encodeURIComponent(url)}`;

      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`
        }
      });
      

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[NewCoverLetterPage] Job details fetch error response:', errorText);
        let errorMsg = 'Failed to fetch job details';
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData.error || errorMsg;
        } catch (e) { /* Ignore parsing error, use default message */ }
        throw new Error(errorMsg);
      }
      
      const jobData = await response.json();

      
      if (!jobData.jobText || jobData.jobText.length < 50) {
        throw new Error('Could not extract sufficient text from the job posting');
      }
      
      const fullJobInfo: JobInfo = {
        title: jobData.title || '',
        company: jobData.company || '',
        description: jobData.description || '',
        location: jobData.location || '',
        rawJobText: jobData.jobText,
        url: url
      };
      

      setJobInfo(fullJobInfo);
      await generateCoverLetter(fullJobInfo);
    } catch (error: any) {
      console.error('[NewCoverLetterPage] Error fetching job details:', error);
      setErrorMessage(error.message || 'Failed to fetch job details');
      setStatus('error');
      processingStarted.current = false; // Reset on error
    }
  };
  
  // Function to generate a cover letter
  const generateCoverLetter = async (jobData: JobInfo) => {
    try {

      setStatus('generating');
      
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        throw new Error('No active session');
      }
      
      // Use REACT_APP_API_URL for the base API path if available
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const apiUrl = `${apiBaseUrl.replace(/\/api$/, '')}/api/cover-letters`;

      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({ 
          jobInfo: {
            title: jobData.title,
            company: jobData.company,
            description: jobData.description,
            location: jobData.location,
            rawJobText: jobData.rawJobText,
            url: jobData.url
          } 
        })
      });
      

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[NewCoverLetterPage] Generate cover letter error response:', errorText);
        let errorMsg = 'Failed to generate cover letter';
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData.error || errorMsg;
        } catch (e) { /* Ignore parsing error, use default message */ }
        throw new Error(errorMsg);
      }
      
      const result = await response.json();

      setCoverLetter(result.cover_letter || result.coverLetter || result.content);
      setStatus('complete');
      
      // Only navigate if we haven't already triggered navigation
      if (!navigateTriggered.current) {
        navigateTriggered.current = true;
        
        // Redirect to the dashboard, highlighting the new letter
        if (result.coverletter_id || result.id) { // Check both possible ID fields
          const letterId = result.coverletter_id || result.id;

          navigate(`/dashboard?latest=${letterId}`, { replace: true });
        } else {
          // No ID found in generation response, redirecting to dashboard without highlight
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error: any) {
      console.error('[NewCoverLetterPage] Error generating cover letter:', error);
      setErrorMessage(error.message || 'Failed to generate cover letter');
      setStatus('error');
      processingStarted.current = false; // Reset on error
      navigateTriggered.current = false; // Reset navigation flag
    }
  };
  
  const retryProcess = () => {

    processingStarted.current = false; // Allow reprocessing
    navigateTriggered.current = false; // Reset navigation flag
    setStatus('idle');
    setErrorMessage(null);
    // Let the effect run again naturally on next render
  };
  
  // Handle loading and error states
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-2 text-lg">Loading your account...</p>
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage || 'An error occurred'}</AlertDescription>
          </Alert>
          
          <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
          <p className="mb-6">We encountered an issue while preparing your cover letter.</p>
          
          {processedUrl && (
            <div className="mb-6">
              <p className="font-medium">Job URL:</p>
              <p className="text-sm break-all">{processedUrl}</p>
            </div>
          )}
          
          <div className="flex space-x-4">
            <Button onClick={retryProcess}>Try Again</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Go to Homepage</Button>
          </div>
        </div>
      </Layout>
    );
  }
  
  // Showing loading states
  let loadingMessage = "";
  if (status === 'fetching-job') {
    loadingMessage = "Fetching job details...";
  } else if (status === 'generating') {
    loadingMessage = "Generating your cover letter...";
  } else if (status === 'idle' && !authLoading) {
    // Show initial loading state if auth is done but processing hasn't started
    loadingMessage = "Preparing your request...";
  }
  
  if (loadingMessage) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 px-4 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{loadingMessage}</h1>
          
          {jobInfo && (
            <div className="mt-6 text-left p-4 border rounded-md bg-background/50">
              <p className="font-medium text-xl">{jobInfo.title}</p>
              <p className="text-muted-foreground">{jobInfo.company}</p>
              {jobInfo.location && <p className="text-sm text-muted-foreground">{jobInfo.location}</p>}
            </div>
          )}
          {processedUrl && !jobInfo && ( // Show URL if we have it but not jobInfo yet
            <div className="mt-6 text-left p-4 border rounded-md bg-background/50">
              <p className="font-medium">Processing URL:</p>
              <p className="text-sm break-all">{processedUrl}</p>
            </div>
          )}
        </div>
      </Layout>
    );
  }
  
  // Fallback/initial view (should rarely be seen if logic flows correctly)
  return (
    <Layout>
      <div className="container max-w-4xl mx-auto py-8 px-4 text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Initializing...</h1>
        <p className="text-muted-foreground">
          {processedUrl ? `Processing ${processedUrl}` : 'Loading job details...'}
        </p>
      </div>
    </Layout>
  );
};

export default NewCoverLetterPage;
